import type {
    Dispatch,
    SyntheticEvent,
    SetStateAction,
} from 'react'
import { useAtomValue } from "jotai"
import InputAdornment from '@mui/material/InputAdornment'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import Autocomplete from "@mui/material/Autocomplete"
import parse from 'autosuggest-highlight/parse'
import match from 'autosuggest-highlight/match'
import TextField from "@mui/material/TextField"
import DiscountRoundedIcon from '@mui/icons-material/DiscountRounded'
import { useParams } from 'react-router-dom'

import { selectedMunicipalityIdAtom } from "@/state"
import { DiscountType, type YearlyFeeDiscount } from "@/types"
import { useYearlyFeeDiscounts } from '@/hooks/api/use-yearly-fee-discount'
import { convertFromCents, printPrice } from '@/helpers'


export default function YearlyFeeDiscountFilter({
    selectedId,
    setSelectedId,
    label,
    hideCustom,
    hideNonMass,
    handleResetPage,
} : {
    selectedId: number | null,
    setSelectedId: Dispatch<SetStateAction<number | null>>,
    label?: string,
    hideCustom?: boolean,
    hideNonMass?: boolean,
    handleResetPage?: () => void,
}) {

    const municipalityId = useAtomValue(selectedMunicipalityIdAtom)

    const { year } = useParams()

    const { data, error } = useYearlyFeeDiscounts(municipalityId, year)

    const filteredDiscounts = (data ?? []).filter(discount => {
        if (hideCustom) {
            if (
                discount.type === DiscountType.householdCustom ||
                discount.type === DiscountType.householdMemberCustom
            ) {
                return false
            }
        }

        if (hideNonMass) {
            if (
                discount.type !== DiscountType.age &&
                discount.type !== DiscountType.inMotivationProgram
            ) {
                return false
            }
        }

        return true
    })

    const getOptionLabel = (option: YearlyFeeDiscount) => (
        `${option.name ?? '-'} - ${option.set_amount_to_zero ? 'Kompletní osvobození' : printPrice(convertFromCents(option.amount_cents))}`
    )

    const options = filteredDiscounts.sort((a, b) => getOptionLabel(a).localeCompare(getOptionLabel(b), 'cs', { numeric: true }))

    return (
        <Autocomplete
            value={
                selectedId
                    ? options.find((option) => {
                        return selectedId === option.id
                    }) ?? null
                    : null
            }
            getOptionLabel={(option) => {
                return getOptionLabel(option)
            }}
            getOptionKey={(option) => {
                return option.id
            }}
            renderOption={(props, option, { inputValue }) => {
                const matches = match(getOptionLabel(option), inputValue)
                const parts = parse(getOptionLabel(option), matches)

                const text = (parts.map((part, index) => (
                    <span
                        key={index}
                        style={{
                            fontWeight: part.highlight ? 700 : 400,
                        }}
                    >
                        {part.text}
                    </span>
                )))

                return (
                    <li {...props} key={props.key}>
                        <div>
                            {text}
                        </div>
                    </li>
                );
            }}
            onChange={(_event: SyntheticEvent, newValue) => {
                if (handleResetPage) {
                    handleResetPage()
                }
                setSelectedId(newValue ? newValue.id : null)
            }}
            id="discount-filter"
            options={options}
            noOptionsText="Žádné slevy nenalezeny"
            renderInput={(params) => (
                <TextField
                    {...params}
                    placeholder={!label ? "Vybrat slevu" : undefined}
                    label={label}
                    size={label ? "medium" : "small"}
                    inputProps={{
                        ...params.inputProps,
                        // disable autocomplete and autofill
                        autoComplete: "off",
                    }}
                    InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                            <InputAdornment position="start" sx={{ paddingLeft: 1}}>
                                {error ?
                                    <ErrorOutlineIcon sx={{ color: 'error.light' }} />
                                    :
                                    <DiscountRoundedIcon sx={{ color: label ? 'customGrey.secondaryText' : 'customGrey.selectorIcon' }} />
                                }
                            </InputAdornment>
                        ),
                      }}
                    autoComplete="off"
                    sx={{
                        minWidth: 250,
                    }}
                />
            )}
        />
    )
}
